import { useState } from 'react';
import { TAILLE_MAX_PHOTO } from '../../components/PhotoBtn.jsx';
import { ScanFacture, TYPES_FACTURE } from '../../components/ScanFacture.jsx';
import { useReglages } from '../../api/reglages.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { CollerListe } from '../../components/CollerListe.jsx';
import { tr } from '../../lib/i18n';
import { articleVersDto, dtoVersArticle } from '../../api/mappers';
import { piecesApi, stockApi } from '../../api/resources';
import { lignesLotDepuisFacture } from '../../lib/facture-fournisseur';
import { prochaineReference } from '../../lib/identifiants';
import { CLE_PACK_IA, packIaActif } from '../../lib/pack-ia';
import { today } from '../../lib/utils';
import { C } from '../../theme/palette';

/** Une ligne vide du tableau. */
const LIGNE_VIDE = { nom: '', qte: '', pa: '', pv: '' };

/** Ne garde que les chiffres et le point décimal (la virgule devient un point). */
const nombreSaisi = (v) =>
  String(v || '')
    .replace(',', '.')
    .replace(/[^0-9.]/g, '');

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 7,
  padding: '8px',
  fontSize: 12,
  minHeight: 40,
};

const etiquette = { fontSize: 10, color: C.muted, marginBottom: 2, fontWeight: 700 };

/**
 * « Ajout en lot » du stock : un tableau de lignes (nom, quantité de départ,
 * prix d'achat, prix de vente) et la facture fournisseur jointe.
 *
 * <p>Chaque article part au serveur avec une référence `MAT-xxx` suivante et
 * le rayon actif. La quantité de départ n'est pas posée comme un champ : elle
 * entre par une entrée datée du journal des achats et par un mouvement de
 * stock, exactement comme un « 📥 Achat » — le journal explique ainsi ce qu'il
 * y a en rayon. La facture, elle, est jointe à chaque article qu'elle a fait
 * entrer, par les pièces jointes du serveur.
 *
 * @param {object} props propriétés du composant
 * @param {object[]} props.stock articles déjà connus (pour la référence suivante)
 * @param {Function} props.setStock mise à jour de la collection d'articles
 * @param {string} [props.rayon] rayon actif ; « Divers » à défaut
 * @param {boolean} props.voitPrixAchat le compte voit-il les prix d'achat ?
 * @param {() => void} props.onFermer fermeture du panneau
 * @returns {import('react').ReactElement} le panneau d'ajout en lot
 */
export function AjoutEnLot({ stock, setStock, rayon, voitPrixAchat, onFermer }) {
  const [lignes, setLignes] = useState([]);
  const [facture, setFacture] = useState(null);
  const [message, setMessage] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const { reglageJson } = useReglages();
  // Avec le Pack IA, la facture jointe est aussi lue : ses lignes remplissent le tableau.
  const avecIa = packIaActif(reglageJson(CLE_PACK_IA, null));

  const change = (ix, cle, valeur) =>
    setLignes((p) => p.map((x, k) => (k === ix ? { ...x, [cle]: valeur } : x)));

  const choisitFacture = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > TAILLE_MAX_PHOTO) {
      setMessage({ ok: false, texte: 'Cette facture dépasse les 10 Mo admis.' });
      return;
    }
    setMessage(null);
    setFacture(f);
  };

  const toutAjouter = async () => {
    const valides = lignes.filter((r) => r.nom.trim());
    if (!valides.length) {
      setMessage({ ok: false, texte: 'Ajoute au moins un article.' });
      return;
    }
    setEnCours(true);
    setMessage(null);
    const crees = [];
    const refuses = [];
    const restantes = [];
    let facturesManquees = 0;
    const cle = (x) =>
      String(x || '')
        .trim()
        .toLowerCase();
    const remis = [];
    for (const [rang, r] of valides.entries()) {
      const qte = +r.qte || 0;
      const pa = +r.pa || 0;
      // Un article déjà en rayon (même nom) reçoit la quantité : coller deux
      // fois la même liste créait chaque article en double.
      const existant = (stock || []).find((a) => cle(a.nom) === cle(r.nom));
      if (existant && typeof existant.id === 'number') {
        try {
          const dto = qte > 0 ? await stockApi.mouvement(existant.id, qte) : null;
          remis.push(dto ? dtoVersArticle(dto) : existant);
        } catch (e) {
          refuses.push(r.nom.trim() + (e?.message ? ' (' + e.message + ')' : ''));
          restantes.push(r);
        }
        continue;
      }
      try {
        let dto = await stockApi.cree(
          articleVersDto({
            ref: prochaineReference('MAT', stock, rang),
            nom: r.nom.trim(),
            description: r.description || null,
            cat: rayon || 'Divers',
            u: r.u || 'pce',
            pa,
            pv: +r.pv || 0,
            stock: 0,
            min: 5,
            // L'entrée datée du journal : elle dit d'où vient la quantité.
            achats: qte > 0 ? [{ date: today(), qte, pa, fournisseur: '' }] : [],
          }),
        );
        if (qte > 0) dto = await stockApi.mouvement(dto.id, qte);
        crees.push(dto);
        if (facture) {
          try {
            await piecesApi.depose('STOCK', dto.id, facture, 'Facture fournisseur — ajout en lot');
          } catch {
            facturesManquees += 1;
          }
        }
      } catch (e) {
        refuses.push(r.nom.trim() + (e?.message ? ' (' + e.message + ')' : ''));
        restantes.push(r);
      }
    }
    if (remis.length) setStock((p) => p.map((a) => remis.find((x) => x.id === a.id) || a));
    if (crees.length) setStock((p) => [...p, ...crees.map(dtoVersArticle)]);
    // Les lignes refusées restent dans le tableau, pour être corrigées.
    setLignes(restantes);
    const morceaux = [];
    if (crees.length) morceaux.push('✓ ' + crees.length + ' article(s) ajouté(s) au stock.');
    if (remis.length)
      morceaux.push('✓ ' + remis.length + ' article(s) déjà en stock : quantité ajoutée.');
    if (crees.length && facture && !facturesManquees)
      morceaux.push('Facture fournisseur jointe à chaque article.');
    if (facturesManquees)
      morceaux.push('La facture n’a pas pu être jointe à ' + facturesManquees + ' article(s).');
    if (refuses.length) morceaux.push('Non ajouté(s) : ' + refuses.join(', ') + '.');
    setMessage({ ok: !refuses.length && !facturesManquees, texte: morceaux.join(' ') });
    if (!refuses.length) setFacture(null);
    setEnCours(false);
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="📷"
        title="Scanner une facture / plusieurs articles"
        color={C.teal}
        sub="Scan → tableau → tu mets juste le prix de vente"
      />
      <div style={{ padding: 13 }}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 9, lineHeight: 1.5 }}>
          Joins ta facture fournisseur : elle est conservée avec chaque article ajouté. Ajoute les
          lignes, avec la <b>quantité</b> reçue{voitPrixAchat ? ' et le prix d’achat' : ''}. Tu n’as
          plus qu’à mettre le <b>prix de vente</b>.
        </div>
        {/* La liste des Notes, de Word ou d'Excel remplit le tableau : on
            vérifie, on complète, puis « Tout ajouter ». */}
        <CollerListe
          choix={
            voitPrixAchat
              ? [
                  ['achat', 'Prix d’achat (hors TVA)'],
                  ['vente', 'Prix de vente (hors TVA)'],
                ]
              : [['vente', 'Prix de vente (hors TVA)']]
          }
          libelleAjout={tr('✓ Mettre dans le tableau')}
          onAjoute={(lues, sorte) =>
            setLignes((p) => [
              ...p.filter((r) => r.nom.trim()),
              ...lues.map((l) => ({
                nom: l.d,
                // La description sous le titre d'un bloc collé.
                description: l.sd || '',
                u: l.u,
                // Un bloc « 49 CHF m2 » donne un prix, pas une quantité reçue.
                qte: l.q && !l.qDefaut ? String(l.q) : '',
                pa: sorte === 'achat' && l.p ? String(l.p) : '',
                pv: sorte === 'vente' && l.p ? String(l.p) : '',
              })),
            ])
          }
        />
        {voitPrixAchat && avecIa && (
          <ScanFacture
            label={
              facture
                ? '📎 Facture scannée ✓ (' + facture.name + ')'
                : '📷 Scanner la facture fournisseur'
            }
            color={facture ? C.green : C.purple}
            style={{ marginBottom: 12 }}
            onPhoto={(_apercu, fichier) => {
              if (fichier.size > TAILLE_MAX_PHOTO) {
                setMessage({ ok: false, texte: 'Cette facture dépasse les 10 Mo admis.' });
                return;
              }
              setMessage(null);
              setFacture(
                fichier.name ? fichier : new File([fichier], 'facture.jpg', { type: fichier.type }),
              );
            }}
            onLecture={(lu) => {
              const lues = lignesLotDepuisFacture(lu);
              if (!lues.length) return 'Aucun article lu sur cette facture.';
              setLignes((p) => [...p.filter((r) => r.nom.trim()), ...lues]);
              return 'Complète le prix de vente de chaque article.';
            }}
          />
        )}
        {voitPrixAchat && !avecIa && (
          <label
            style={{
              display: 'block',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 800,
              color: facture ? C.green : C.purple,
              border: '1.5px solid ' + (facture ? C.green : C.purple),
              borderRadius: 9,
              padding: '10px',
              textAlign: 'center',
              marginBottom: 12,
            }}
          >
            {facture
              ? '📎 Facture scannée ✓ — remplis le tableau ci-dessous (' + facture.name + ')'
              : '📷 Scanner / joindre la facture fournisseur'}
            <input
              type="file"
              accept={TYPES_FACTURE}
              aria-label="Facture fournisseur"
              style={{ display: 'none' }}
              onChange={choisitFacture}
            />
          </label>
        )}
        {lignes.length === 0 && (
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '6px 0 10px',
            }}
          >
            Aucun article pour l’instant — touche « ＋ Ajouter une ligne ».
          </div>
        )}
        {lignes.map((r, ix) => (
          <div
            key={ix}
            style={{
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '8px',
              marginBottom: 7,
            }}
          >
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <input
                value={r.nom}
                onChange={(e) => change(ix, 'nom', e.target.value)}
                placeholder="Nom de l’article"
                aria-label={'Nom de l’article, ligne ' + (ix + 1)}
                style={{ ...champ, flex: 1 }}
              />
              <button
                type="button"
                aria-label={'Retirer la ligne ' + (ix + 1)}
                onClick={() => setLignes((p) => p.filter((_, k) => k !== ix))}
                style={{
                  fontSize: 14,
                  color: C.red,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  minWidth: 32,
                  minHeight: 40,
                }}
              >
                ✕
              </button>
            </div>
            {r.description && (
              <div style={{ fontSize: 10, color: C.grey, margin: '-2px 2px 6px' }}>
                {r.description}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <label style={{ flex: 1 }}>
                <div style={etiquette}>Quantité de départ</div>
                <input
                  value={r.qte}
                  onChange={(e) => change(ix, 'qte', nombreSaisi(e.target.value))}
                  inputMode="decimal"
                  placeholder="0"
                  style={champ}
                />
              </label>
              {voitPrixAchat && (
                <label style={{ flex: 1 }}>
                  <div style={etiquette}>Prix d’achat</div>
                  <input
                    value={r.pa}
                    onChange={(e) => change(ix, 'pa', nombreSaisi(e.target.value))}
                    inputMode="decimal"
                    placeholder="0"
                    style={champ}
                  />
                </label>
              )}
              <label style={{ flex: 1 }}>
                <div style={{ ...etiquette, color: C.teal, fontWeight: 800 }}>Prix de vente</div>
                <input
                  value={r.pv}
                  onChange={(e) => change(ix, 'pv', nombreSaisi(e.target.value))}
                  inputMode="decimal"
                  placeholder="0"
                  style={{ ...champ, border: '1.5px solid ' + C.teal }}
                />
              </label>
            </div>
          </div>
        ))}
        <Btn
          label="＋ Ajouter une ligne"
          sm
          ghost
          color={C.navy}
          full
          onClick={() => setLignes((p) => [...p, { ...LIGNE_VIDE }])}
        />
        {message && (
          <div
            role="status"
            style={{
              background: message.ok ? C.greenL : C.redL,
              border: '1px solid ' + (message.ok ? C.green : C.red) + '30',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 11,
              color: message.ok ? C.green : C.red,
              marginTop: 8,
            }}
          >
            {message.texte}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <Btn
              label={enCours ? 'Ajout en cours…' : '✓ Tout ajouter au stock'}
              color={C.teal}
              full
              disabled={enCours || !lignes.some((r) => r.nom.trim())}
              onClick={toutAjouter}
            />
          </div>
          <Btn label="Fermer" sm ghost color={C.grey} onClick={onFermer} />
        </div>
      </div>
    </Card>
  );
}
